/**
 * Owner-view Analysis tab — co-located section blocks (prefix: Analysis*).
 *
 * Rebuilds the `voice-owner-analysis` wireframe blocks on-brand: a friendly
 * headline number, a simple 7-day bar chart, the topics callers ask about most,
 * and two calm tiles (resolution + how people felt). Read-only viewer; the only
 * mutating affordance in this flow ("Tell our genie") lives in the shared shell.
 *
 * Matches OverviewTab's SectionCard rhythm so the four owner tabs feel like one
 * surface. Brand law: genie tokens only; signature gradient for the chart bars;
 * wireframe green (Live/Active) -> genie-success; "Help Genie" two words;
 * *Voice* italic in headings; no "AI" language; no em dashes.
 */
import { TrendingUp } from "lucide-react";
import {
  BarChart7d,
  SectionCard,
  StatTile,
  TopicBar,
  TOPIC_FILLS,
} from "@/components/voice-genie/atoms";
import type { GenieAnalysis } from "@/types/voice-vision";

const BLUE = "#4E9CFF";
const PINK = "#FF4DB8";

/* ───────────────────────────── headline ─────────────────────────────── */

const FEELING_COPY: Record<GenieAnalysis["feeling"], string> = {
  happy: "Happy",
  neutral: "Steady",
  mixed: "Mixed",
  unhappy: "Tense",
};

export const AnalysisHeadline = ({ data }: { data: GenieAnalysis }) => {
  if (data.handledThisMonth === 0) {
    return (
      <div
        data-hg-block="headline"
        className="rounded-[18px] border border-genie-border bg-white shadow-sm px-6 md:px-7 py-6"
      >
        <p className="text-[13px] font-semibold text-gray-500">
          This month, your genie handled
        </p>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2 mt-1.5">
          <span className="text-[44px] leading-none font-extrabold text-[#1A2238] tracking-[-1.2px]">
            0
          </span>
          <span className="text-[17px] font-bold text-[#64748B]">
            conversations yet
          </span>
        </div>
        <p className="text-[13px] text-gray-500 mt-2.5">
          As soon as people start talking with your genie, the numbers land here.
        </p>
      </div>
    );
  }

  return (
    <div
      data-hg-block="headline"
      className="rounded-[18px] border border-genie-border bg-white shadow-sm px-6 md:px-7 py-6"
    >
      <p className="text-[13px] font-semibold text-gray-500">
        This month, your genie handled
      </p>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2 mt-1.5">
        <span className="text-[44px] leading-none font-extrabold text-[#1A2238] tracking-[-1.2px]">
          {data.handledThisMonth.toLocaleString()}
        </span>
        <span className="text-[17px] font-bold text-[#64748B]">conversations</span>
        {data.changePct !== 0 && (
          <span
            className={`inline-flex items-center gap-1 h-6 pl-2 pr-2.5 rounded-full ${
              data.changePct > 0
                ? "bg-genie-success/10 text-genie-success"
                : "bg-gray-100 text-gray-500"
            }`}
          >
            <TrendingUp
              className={`w-3 h-3 ${data.changePct < 0 ? "rotate-180" : ""}`}
            />
            <span className="text-[12px] font-bold">
              {data.changePct > 0 ? "+" : ""}
              {data.changePct}%
            </span>
          </span>
        )}
      </div>
      {data.hoursSaved > 0 && (
        <p className="text-[13px] text-gray-500 mt-2.5">
          That's around {data.hoursSaved} hours of calls and chats it took off your
          plate.
        </p>
      )}
    </div>
  );
};

/* ──────────────────────────── 7-day chart ───────────────────────────── */

export const AnalysisWeekChart = ({ data }: { data: GenieAnalysis }) => (
  <div data-hg-block="week-chart">
    <SectionCard
      genieIcon="trending-up"
      tint={BLUE}
      title="Last 7 days"
      subtitle="How busy your genie has been, day by day"
      className="mt-[18px]"
    >
      {data.last7Days.length === 0 ? (
        <p className="text-sm text-gray-500 py-6 text-center">
          No conversations in the last 7 days yet.
        </p>
      ) : (
        <BarChart7d data={data.last7Days} height={172} />
      )}
    </SectionCard>
  </div>
);

/* ─────────────────────── what people ask about ──────────────────────── */

export const AnalysisTopics = ({ data }: { data: GenieAnalysis }) => (
  <div data-hg-block="top-topics">
    <SectionCard
      genieIcon="chat"
      tint={PINK}
      title="What people ask about most"
      subtitle="The questions callers bring up again and again"
      className="mt-[18px]"
    >
      {data.topTopics.length === 0 ? (
        <p className="text-sm text-gray-500 py-6 text-center">
          We will surface caller topics here once conversations roll in.
        </p>
      ) : (
        <div className="flex flex-col gap-3.5">
          {data.topTopics.map((topic, i) => (
            <TopicBar
              key={topic.label}
              label={topic.label}
              pct={topic.pct}
              fillClassName={TOPIC_FILLS[i % TOPIC_FILLS.length]}
            />
          ))}
          {data.sampleTruncated && data.sampledConversations > 0 && (
            <p className="text-[12px] text-gray-400 pt-1">
              From your most recent {data.sampledConversations} conversations.
            </p>
          )}
        </div>
      )}
    </SectionCard>
  </div>
);

/* ──────────────────────────── two tiles ─────────────────────────────── */

const FEELING_EMOJI: Record<GenieAnalysis["feeling"], string> = {
  happy: "🙂",
  neutral: "😌",
  mixed: "😐",
  unhappy: "😕",
};

export const AnalysisTiles = ({ data }: { data: GenieAnalysis }) => (
  <div data-hg-block="tiles" className="mt-[18px] flex flex-col sm:flex-row gap-4">
    {data.hasResolution ? (
      <StatTile
        label="Sorted without you"
        value={`${data.resolvedWithoutHuman}%`}
        note={data.resolvedNote}
        valueClassName="text-genie-success"
      />
    ) : (
      <StatTile
        label="Sorted without you"
        value={<span className="text-gray-300">—</span>}
        note="Not enough conversations yet to read this."
        valueClassName="text-gray-300"
      />
    )}
    {data.hasSentiment ? (
      <StatTile
        label="How people felt"
        value={
          <span className="inline-flex items-center gap-2 text-genie-primary">
            {FEELING_COPY[data.feeling]}
            <span aria-hidden className="text-[28px] leading-none">
              {FEELING_EMOJI[data.feeling]}
            </span>
          </span>
        }
        note={
          data.rating > 0
            ? `Callers rated it ${data.rating} out of 5.`
            : "Based on how conversations have been going."
        }
        valueClassName="text-genie-primary"
      />
    ) : (
      <StatTile
        label="How people felt"
        value={<span className="text-gray-300">—</span>}
        note="We will read the mood once a few conversations land."
        valueClassName="text-gray-300"
      />
    )}
  </div>
);

/* ─────────────────────── loading + empty states ─────────────────────── */

export const AnalysisSkeleton = () => (
  <div className="w-full">
    {/* headline skeleton */}
    <div className="rounded-[18px] border border-genie-border bg-white shadow-sm px-6 md:px-7 py-6">
      <div className="h-3.5 w-44 rounded bg-gray-100 animate-pulse" />
      <div className="flex items-baseline gap-3 mt-3">
        <div className="h-10 w-24 rounded-lg bg-gray-100 animate-pulse" />
        <div className="h-5 w-28 rounded bg-gray-100 animate-pulse" />
      </div>
      <div className="h-3 w-2/3 rounded bg-gray-100 animate-pulse mt-3" />
    </div>

    {/* chart + topics section-card skeletons */}
    {[0, 1].map((i) => (
      <div
        key={i}
        className="mt-[18px] p-[1.5px] rounded-[18px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-sm"
      >
        <div className="rounded-[16.5px] bg-white">
          <div className="flex items-center gap-3 px-5 md:px-6 py-4 border-b border-[#F3F4F6]">
            <div className="w-10 h-10 rounded-[11px] bg-gray-100 animate-pulse" />
            <div className="flex-1">
              <div className="h-4 w-40 rounded bg-gray-100 animate-pulse" />
              <div className="h-2.5 w-56 rounded bg-gray-100 animate-pulse mt-2" />
            </div>
          </div>
          <div className="px-5 md:px-6 pt-4 pb-5">
            {i === 0 ? (
              <div className="flex items-end gap-3.5 h-[150px]">
                {[40, 70, 55, 90, 80, 30, 20].map((h, k) => (
                  <div
                    key={k}
                    className="flex-1 rounded-lg bg-gray-100 animate-pulse"
                    style={{ height: `${h}%` }}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-3.5">
                {[0, 1, 2, 3, 4].map((r) => (
                  <div key={r} className="flex flex-col gap-1.5">
                    <div className="flex justify-between">
                      <div className="h-3.5 w-1/2 rounded bg-gray-100 animate-pulse" />
                      <div className="h-3.5 w-8 rounded bg-gray-100 animate-pulse" />
                    </div>
                    <div className="h-2 w-full rounded-full bg-gray-100 animate-pulse" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    ))}

    {/* two-tile skeleton */}
    <div className="mt-[18px] flex flex-col sm:flex-row gap-4">
      {[0, 1].map((i) => (
        <div
          key={i}
          className="flex-1 rounded-[18px] border border-genie-border bg-white shadow-sm px-5 py-5"
        >
          <div className="h-3 w-28 rounded bg-gray-100 animate-pulse" />
          <div className="h-8 w-20 rounded-lg bg-gray-100 animate-pulse mt-3" />
          <div className="h-3 w-36 rounded bg-gray-100 animate-pulse mt-3" />
        </div>
      ))}
    </div>
  </div>
);
